// client/src/hooks/useFranquiaSubscriptions.js
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import useAlert from './useAlert';
import { ME_QUERY_KEY } from './useAuth';

export const FRANQUIA_SUBSCRIPTION_QUERY_KEY = ['franquiaSubscriptions'];

export const useMutateFranquiaSubscriptions = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  const invalidateCaches = () => {
    queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: FRANQUIA_SUBSCRIPTION_QUERY_KEY });
  };

  const { mutateAsync: createFranquiaSubscription, ...createMutation } = useMutation({
    // ✅ AJUSTE: Adicionado 'billingAddress' aos parâmetros da função.
    mutationFn: ({ franquiaId, planId, cardData, billingAddress }) =>
      // ✅ AJUSTE: 'billingAddress' é enviado no corpo da requisição.
      apiClient.post('/franquias-subscriptions', { franquiaId, planId, cardData, billingAddress }),
    onSuccess: () => {
      invalidateCaches();
      setAlert('Assinatura da franquia realizada com sucesso!', 'success');
    },
    onError: (err) =>
      setAlert(err.response?.data?.error || err.message || 'Erro ao criar assinatura da franquia.', 'danger'),
  });

  const { mutateAsync: changePlan, ...changeMutation } = useMutation({
    // ✅ AJUSTE: Adicionado 'billingAddress' aos parâmetros da função.
    mutationFn: ({ subscriptionId, newPlanId, billingAddress }) =>
      // ✅ AJUSTE: 'billingAddress' é enviado no corpo da requisição.
      apiClient.patch(`/franquias-subscriptions/${subscriptionId}/change-plan`, { newPlanId, billingAddress }),
    onSuccess: () => {
      invalidateCaches();
      setAlert('Plano da franquia alterado com sucesso!', 'success');
    },
    onError: (err) =>
      setAlert(err.response?.data?.error || err.message || 'Erro ao alterar o plano.', 'danger'),
  });

  const { mutateAsync: updatePaymentMethod, ...updateMutation } = useMutation({
    // ✅ AJUSTE: Adicionado 'billingAddress' aos parâmetros da função.
    mutationFn: ({ subscriptionId, cardData, billingAddress }) =>
      // ✅ AJUSTE: 'billingAddress' é enviado no corpo da requisição.
      apiClient.patch(`/franquias-subscriptions/${subscriptionId}/payment-method`, { cardData, billingAddress }),
    onSuccess: () => {
      invalidateCaches();
      setAlert('Método de pagamento atualizado com sucesso!', 'success');
    },
    onError: (err) =>
      setAlert(err.response?.data?.error || err.message || 'Erro ao atualizar o método de pagamento.', 'danger'),
  });

  // 🔑 Cancelamento em 2 etapas (primeiro agenda, depois pode optar pelo imediato)
  const { mutateAsync: cancelSubscription, ...cancelMutation } = useMutation({
    mutationFn: async ({ subscriptionId, immediate = false }) => {
      if (!window.confirm(
        `Tem certeza que deseja ${immediate ? 'cancelar imediatamente' : 'agendar o cancelamento'} da assinatura desta franquia?`
      )) {
        throw new Error('Ação cancelada pelo usuário');
      }
      const response = await apiClient.delete(
        `/franquias-subscriptions/${subscriptionId}?immediate=${immediate}`
      );
      return response.data; // { status, canceledAt, currentPeriodEnd, message }
    },
    onSuccess: (data) => {
      invalidateCaches();
      setAlert(data.message, 'success');
      return data;
    },
    onError: (err) => {
      if (err.message !== 'Ação cancelada pelo usuário') {
        setAlert(err.response?.data?.error || err.message || 'Erro ao cancelar a assinatura.', 'danger');
      }
    },
  });

  return {
    createFranquiaSubscription,
    changePlan,
    updatePaymentMethod,
    cancelSubscription,
    isLoading:
      createMutation.isPending ||
      changeMutation.isPending ||
      updateMutation.isPending ||
      cancelMutation.isPending,
    error:
      createMutation.error ||
      changeMutation.error ||
      updateMutation.error ||
      cancelMutation.error,
    mutations: { createMutation, changeMutation, updateMutation, cancelMutation },
  };
};
